/**
 * LocalCameraCapture - Device camera capture component
 * Like GoodEye app - uses browser's camera for capturing photos
 * Used for: incident documentation, check-in verification, visitor photos, patrol evidence
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Camera, X, Check, RotateCcw, SwitchCamera, 
  FlashlightOff, Flashlight, Image, Download, Trash2, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const LocalCameraCapture = ({ 
  isOpen, 
  onClose, 
  onCapture,  // Called with base64 image data
  title = "Capture Photo",
  aspectRatio = "4/3",  // "4/3", "16/9", "1/1"
  quality = 0.85,  // JPEG quality
  maxWidth = 1920,  // Max image width
  showGallery = true,  // Show captured images gallery
  multipleCaptures = false,  // Allow multiple photos
  guideLine = null  // Optional guide overlay text
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Initialize camera on mount
  useEffect(() => {
    if (isOpen) {
      initializeCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode, activeCamera]);

  const initializeCamera = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get available cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setCameras(videoDevices);
      
      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Build constraints
      const constraints = {
        video: {
          facingMode: activeCamera ? undefined : facingMode,
          deviceId: activeCamera ? { exact: activeCamera } : undefined,
          width: { ideal: maxWidth },
          height: { ideal: Math.round(maxWidth * (aspectRatio === '16/9' ? 9/16 : aspectRatio === '1/1' ? 1 : 3/4)) }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Camera error:', err);
      setError(err.name === 'NotAllowedError' 
        ? 'Camera access denied. Please allow camera permissions.'
        : 'Failed to access camera. Please try again.');
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const switchCamera = useCallback(() => {
    if (cameras.length > 1) {
      // Find next camera
      const currentIdx = cameras.findIndex(c => c.deviceId === activeCamera);
      const nextIdx = (currentIdx + 1) % cameras.length;
      setActiveCamera(cameras[nextIdx].deviceId);
    } else {
      // Toggle facing mode
      setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    }
  }, [cameras, activeCamera]);

  const toggleFlash = useCallback(async () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track.getCapabilities && track.getCapabilities().torch) {
        try {
          await track.applyConstraints({
            advanced: [{ torch: !flashEnabled }]
          });
          setFlashEnabled(!flashEnabled);
        } catch (err) {
          console.log('Flash not supported on this device');
        }
      }
    }
  }, [flashEnabled]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas size based on video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Draw the video frame
    ctx.drawImage(video, 0, 0);
    
    // Add timestamp watermark
    const now = new Date();
    const timestamp = now.toLocaleString();
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = 2;
    const textWidth = ctx.measureText(timestamp).width;
    ctx.strokeText(timestamp, canvas.width - textWidth - 10, canvas.height - 10);
    ctx.fillText(timestamp, canvas.width - textWidth - 10, canvas.height - 10);
    
    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', quality);
    
    if (multipleCaptures) {
      setCapturedImages(prev => [...prev, { 
        id: Date.now(), 
        data: imageData,
        timestamp: now.toISOString()
      }]);
    } else {
      setPreviewImage(imageData);
    }
    
    // Flash effect
    setTimeout(() => setIsCapturing(false), 100);
    
    // Play capture sound (using Web Audio API)
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 1000;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Audio not supported
    }
  }, [quality, multipleCaptures]);

  const confirmCapture = useCallback(() => {
    if (multipleCaptures) {
      onCapture(capturedImages.map(img => img.data));
    } else if (previewImage) {
      onCapture(previewImage);
    }
    handleClose();
  }, [previewImage, capturedImages, multipleCaptures, onCapture]);

  const retakePhoto = useCallback(() => {
    setPreviewImage(null);
  }, []);

  const removeImage = useCallback((id) => {
    setCapturedImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedImages([]);
    setPreviewImage(null);
    setError(null);
    onClose();
  }, [onClose]);

  const downloadImage = useCallback((imageData) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `capture_${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded');
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 bg-slate-900 text-white">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative bg-black">
          {/* Camera View or Preview */}
          {previewImage ? (
            <div className="relative">
              <img 
                src={previewImage} 
                alt="Captured" 
                className="w-full object-contain max-h-[60vh]"
              />
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={retakePhoto}
                  className="bg-white/90 hover:bg-white"
                >
                  <RotateCcw className="h-5 w-5 mr-2" />
                  Retake
                </Button>
                <Button
                  size="lg"
                  onClick={confirmCapture}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Use Photo
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Video Feed */}
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full ${isCapturing ? 'brightness-150' : ''}`}
                  style={{ aspectRatio }}
                />
                
                {/* Loading Overlay */}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center text-white">
                      <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3" />
                      <p>Starting camera...</p>
                    </div>
                  </div>
                )}
                
                {/* Error Overlay */}
                {error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center text-white p-6">
                      <Camera className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-red-400 mb-4">{error}</p>
                      <Button onClick={initializeCamera}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Guide Line Overlay */}
                {guideLine && !isLoading && !error && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                    {guideLine}
                  </div>
                )}
                
                {/* Capture Overlay */}
                {isCapturing && (
                  <div className="absolute inset-0 bg-white/30 animate-pulse" />
                )}
              </div>
              
              {/* Camera Controls */}
              {!isLoading && !error && (
                <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
                  {/* Switch Camera */}
                  {cameras.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={switchCamera}
                      className="bg-white/20 hover:bg-white/40 text-white h-12 w-12 rounded-full"
                    >
                      <SwitchCamera className="h-6 w-6" />
                    </Button>
                  )}
                  
                  {/* Capture Button */}
                  <button
                    onClick={capturePhoto}
                    className="h-16 w-16 rounded-full bg-white border-4 border-gray-300 hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-blue-500/50"
                    data-testid="capture-btn"
                  >
                    <div className="h-full w-full rounded-full bg-white hover:bg-gray-100" />
                  </button>
                  
                  {/* Flash Toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleFlash}
                    className="bg-white/20 hover:bg-white/40 text-white h-12 w-12 rounded-full"
                  >
                    {flashEnabled ? (
                      <Flashlight className="h-6 w-6 text-yellow-400" />
                    ) : (
                      <FlashlightOff className="h-6 w-6" />
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
          
          {/* Hidden canvas for capturing */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        
        {/* Gallery for multiple captures */}
        {multipleCaptures && capturedImages.length > 0 && (
          <div className="p-4 bg-slate-100 border-t">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                Captured Photos ({capturedImages.length})
              </span>
              <Button
                size="sm"
                onClick={confirmCapture}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-1" />
                Done
              </Button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {capturedImages.map(img => (
                <div key={img.id} className="relative flex-shrink-0 group">
                  <img 
                    src={img.data} 
                    alt="Captured" 
                    className="h-20 w-20 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-white hover:bg-white/20"
                      onClick={() => downloadImage(img.data)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:bg-white/20"
                      onClick={() => removeImage(img.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="absolute top-3 right-3 text-white hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default LocalCameraCapture;
